import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { Lock, Eye, EyeOff, ArrowLeft, KeyRound, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resetPassword } from "@/lib/api/auth";
import { getErrorMessage } from "@/lib/api/client";

import cityBackground from "@/assets/city_logissimo.jpg";
import logoRvb from "@/assets/logo-rvb.png";
import logoReserve from "@/assets/logo-reserve.png";

const searchSchema = z.object({
  token: z.string().catch(""),
});

export const Route = createFileRoute('/_auth/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
})

const formSchema = z.object({
  password: z.string().min(8, "Le mot de passe doit contenir au moins 8 caractères"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type FormData = z.infer<typeof formSchema>;

function FloatingHexagon({ 
  className, 
  delay = 0,
  duration = 20,
  size = 60,
  path = "vertical"
}: { 
  className?: string;
  delay?: number;
  duration?: number;
  size?: number;
  path?: "vertical" | "diagonal" | "curved";
}) {
  const pathVariants = {
    vertical: {
      y: [0, -100, -200, -300],
      x: [0, 0, 0, 0],
    },
    diagonal: {
      y: [0, -80, -160, -240],
      x: [0, 30, 10, 40],
    },
    curved: {
      y: [0, -60, -140, -220],
      x: [0, 40, -20, 30],
    },
  };

  return (
    <motion.div
      className={`absolute pointer-events-none ${className}`}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: [0, 0.2, 0.15, 0],
        scale: [0.8, 1, 1.1, 0.9],
        rotate: [0, 90, 180, 270],
        ...pathVariants[path],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <svg width={size} height={size * 1.15} viewBox="0 0 60 69" fill="none">
        <path
          d="M30 0L57.3013 15.75V47.25L30 63L2.69873 47.25V15.75L30 0Z"
          stroke={`url(#hexGradientReset-${delay})`}
          strokeWidth="2"
          fill="none"
        />
        <defs>
          <linearGradient id={`hexGradientReset-${delay}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFCC00" />
            <stop offset="50%" stopColor="#00B2A9" />
            <stop offset="100%" stopColor="#0066B3" />
          </linearGradient>
        </defs>
      </svg>
    </motion.div>
  );
}

function SuccessAnimation() {
  return (
    <motion.div
      className="flex flex-col items-center gap-4"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 15 }}
    >
      <motion.div 
        className="w-20 h-20 rounded-full bg-gradient-to-br from-laposte-teal to-laposte-teal-dark flex items-center justify-center shadow-lg shadow-laposte-teal/30"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
      >
        <motion.div
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Check className="w-10 h-10 text-white" strokeWidth={3} />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

const shakeAnimation = {
  x: [0, -10, 10, -10, 10, -5, 5, 0],
  transition: { duration: 0.5 }
};

function ResetPasswordPage() {
  const { token } = useSearch({ from: '/_auth/reset-password' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const hasToken = Boolean(token);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
    mouseX.set(x * 15);
    mouseY.set(y * 15);
  }, [mouseX, mouseY]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShouldShake(true);
      const timer = setTimeout(() => setShouldShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [errors]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await resetPassword(token, data.password);
      setIsSuccess(true);
      toast.success("Mot de passe modifié", {
        description: "Vous pouvez maintenant vous connecter",
      });
    } catch (error) {
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
      toast.error("Erreur", {
        description: getErrorMessage(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image with parallax effect */}
      <div 
        className="hidden lg:flex lg:w-[55%] relative overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        <motion.img
          src={cityBackground}
          alt="Logissimo"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            scale: 1.1,
            x: springX,
            y: springY,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-laposte-navy/90 via-laposte-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
        
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{
            background: "linear-gradient(90deg, #FFCC00, #00B2A9, #0066B3, #FFCC00)",
            backgroundSize: "200% 100%",
          }}
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ 
            scaleX: 1,
            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
          }}
          transition={{ 
            scaleX: { duration: 1.2, delay: 0.5, ease: "easeOut" },
            backgroundPosition: { duration: 5, repeat: Infinity, ease: "linear", delay: 1.7 }
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src={logoReserve} alt="Logissimo" className="h-14 w-auto drop-shadow-lg" />
          </motion.div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <motion.div 
                  className="w-16 h-1 rounded-full"
                  style={{ 
                    background: "linear-gradient(90deg, #FFCC00, #00B2A9, #FFCC00)",
                    backgroundSize: "200% 100%",
                  }}
                  initial={{ width: 0 }}
                  animate={{ 
                    width: 64,
                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                  }}
                  transition={{ 
                    width: { duration: 0.6, delay: 0.6 },
                    backgroundPosition: { duration: 3, repeat: Infinity, ease: "linear", delay: 1.2 }
                  }}
                />
                <h2 className="text-5xl font-bold leading-tight tracking-tight">
                  Logissimo<br />
                  <span className="text-laposte-yellow">Pricemaker</span>
                </h2>
              </div>
              <p className="text-xl text-white/80 max-w-lg leading-relaxed">
                Établissez vos cotations et créez des propositions commerciales personnalisées.
              </p>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="text-sm text-white/50"
          >
            © {new Date().getFullYear()} Logissimo - Tous droits réservés
          </motion.p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative overflow-hidden bg-background">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-laposte-blue/5" />
        
        <motion.div 
          className="lg:hidden absolute top-0 left-0 right-0 h-32 gradient-logissimo opacity-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1 }}
        />
        
        <FloatingHexagon className="left-[10%] bottom-[20%]" delay={0} size={40} path="vertical" />
        <FloatingHexagon className="right-[15%] bottom-[30%]" delay={5} size={30} path="diagonal" />
        <FloatingHexagon className="left-[20%] bottom-[10%]" delay={10} size={50} path="curved" />
        <FloatingHexagon className="right-[8%] bottom-[45%]" delay={7} size={35} path="curved" />
        
        <motion.div 
          className="absolute top-1/4 -left-32 w-96 h-96 rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, #FFCC00 0%, transparent 70%)" }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ background: "radial-gradient(circle, #00B2A9 0%, transparent 70%)" }}
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.15, 0.25, 0.15],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mb-10 lg:hidden flex justify-center"
          >
            <img src={logoRvb} alt="Logissimo" className="h-12 w-auto" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={shouldShake ? shakeAnimation : { opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-2xl shadow-black/5 dark:shadow-black/20"
          >
            <AnimatePresence mode="wait">
              {!hasToken ? (
                <motion.div
                  key="no-token"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="text-center mb-6">
                    <motion.div
                      className="flex flex-col items-center gap-4"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                      <motion.div 
                        className="w-20 h-20 rounded-full bg-gradient-to-br from-destructive/80 to-destructive flex items-center justify-center shadow-lg shadow-destructive/30"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                      >
                        <AlertTriangle className="w-10 h-10 text-white" strokeWidth={2} />
                      </motion.div>
                    </motion.div>
                    <motion.h1 
                      className="text-3xl font-bold mt-6"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      Lien invalide
                    </motion.h1>
                    <motion.p 
                      className="text-muted-foreground mt-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      Ce lien de réinitialisation est invalide ou a expiré
                    </motion.p>
                  </div>

                  <motion.div 
                    className="space-y-4"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button 
                      asChild 
                      className="w-full h-12 text-base font-semibold bg-gradient-to-r from-laposte-yellow to-laposte-yellow-dark hover:from-laposte-yellow-dark hover:to-laposte-yellow text-laposte-navy shadow-lg shadow-laposte-yellow/20 hover:shadow-laposte-yellow/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                      size="lg"
                    >
                      <Link to="/forgot-password">
                        Demander un nouveau lien
                      </Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="ghost" 
                      className="w-full h-11 text-muted-foreground hover:text-foreground"
                    >
                      <Link to="/login">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Retour à la connexion
                      </Link>
                    </Button>
                  </motion.div>
                </motion.div>
              ) : isSuccess ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="text-center mb-6">
                    <SuccessAnimation />
                    <motion.h1 
                      className="text-3xl font-bold mt-6"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      Mot de passe modifié !
                    </motion.h1>
                    <motion.p 
                      className="text-muted-foreground mt-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      Votre mot de passe a été réinitialisé avec succès
                    </motion.p>
                  </div>

                  <motion.div 
                    className="space-y-6"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button 
                      asChild 
                      className="w-full h-12 text-base font-semibold bg-gradient-to-r from-laposte-yellow to-laposte-yellow-dark hover:from-laposte-yellow-dark hover:to-laposte-yellow text-laposte-navy shadow-lg shadow-laposte-yellow/20 hover:shadow-laposte-yellow/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                      size="lg"
                    >
                      <Link to="/login">
                        Se connecter
                      </Link>
                    </Button>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <div className="text-center mb-8 space-y-2">
                    <motion.h1 
                      className="text-3xl font-bold"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      Nouveau mot de passe
                    </motion.h1>
                    <motion.p 
                      className="text-muted-foreground"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      Choisissez un nouveau mot de passe pour votre compte
                    </motion.p>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <motion.div 
                      className="space-y-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Label htmlFor="password" className="text-sm font-medium">Nouveau mot de passe</Label>
                      <div className="relative group input-glow-wrapper">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-laposte-yellow transition-colors duration-200" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Minimum 8 caractères"
                          autoFocus
                          autoComplete="new-password"
                          aria-describedby={errors.password ? "password-error" : undefined}
                          className="pl-11 pr-11 h-12 bg-background/50 border-border/50 focus:border-laposte-yellow/50 focus:ring-laposte-yellow/20 focus:shadow-[0_0_0_4px_rgba(255,204,0,0.1),0_0_20px_rgba(255,204,0,0.05)] transition-all duration-200"
                          {...register("password")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.password && (
                        <motion.p 
                          id="password-error"
                          className="text-sm text-destructive"
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          role="alert"
                        >
                          {errors.password.message}
                        </motion.p>
                      )}
                    </motion.div>

                    <motion.div 
                      className="space-y-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirmer le mot de passe</Label>
                      <div className="relative group input-glow-wrapper">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-laposte-yellow transition-colors duration-200" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Retapez le mot de passe"
                          autoComplete="new-password"
                          aria-describedby={errors.confirmPassword ? "confirm-password-error" : undefined}
                          className="pl-11 pr-11 h-12 bg-background/50 border-border/50 focus:border-laposte-yellow/50 focus:ring-laposte-yellow/20 focus:shadow-[0_0_0_4px_rgba(255,204,0,0.1),0_0_20px_rgba(255,204,0,0.05)] transition-all duration-200"
                          {...register("confirmPassword")}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <motion.p 
                          id="confirm-password-error"
                          className="text-sm text-destructive"
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          role="alert"
                        >
                          {errors.confirmPassword.message}
                        </motion.p>
                      )}
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="space-y-3"
                    >
                      <Button
                        type="submit"
                        className="w-full h-12 text-base font-semibold bg-gradient-to-r from-laposte-yellow to-laposte-yellow-dark hover:from-laposte-yellow-dark hover:to-laposte-yellow text-laposte-navy shadow-lg shadow-laposte-yellow/20 hover:shadow-laposte-yellow/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                        size="lg"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <motion.div 
                            className="w-5 h-5 border-2 border-laposte-navy border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                        ) : (
                          <span className="flex items-center gap-2">
                            <KeyRound className="h-4 w-4" />
                            Réinitialiser le mot de passe
                          </span>
                        )}
                      </Button>

                      <Button 
                        asChild 
                        variant="ghost" 
                        className="w-full h-11 text-muted-foreground hover:text-foreground"
                      >
                        <Link to="/login">
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          Retour à la connexion
                        </Link>
                      </Button>
                    </motion.div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.p 
            className="text-center text-sm text-muted-foreground mt-8 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            © {new Date().getFullYear()} Logissimo Pricemaker
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
